import React from 'react';
import { X } from 'lucide-react';
import { Option } from './SearchableSelect';

interface MultiSelectChipsProps {
  value: Option[];
  onChange: (value: Option[]) => void;
  options?: Option[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  maxHeightClassName?: string;
  /**
   * When set, only the first `maxVisible` chips are shown inline.
   * A non-removable "+N more" badge is appended for the remaining items.
   * No scroll is applied when this prop is provided.
   */
  maxVisible?: number;
  /**
   * Optional function that maps an Option to a URL string.
   * When provided, the chip label is rendered as an anchor (<a>) tag so the
   * user can right-click → "Open in new tab" (matching Legacy DRT behaviour).
   * The remove (×) button still works normally — clicking it removes the chip
   * without navigating.
   */
  getItemUrl?: (item: Option) => string | undefined;
}

export const MultiSelectChips: React.FC<MultiSelectChipsProps> = ({
  value,
  onChange,
  placeholder = 'Select options',
  disabled = false,
  className = '',
  maxHeightClassName = 'max-h-[80px]',
  maxVisible,
  getItemUrl,
}) => {
  const handleRemove = (optionId: string | number) => {
    if (disabled) {
      return;
    }

    onChange(value.filter((item) => item.id !== optionId));
  };

  if (value.length === 0) {
    return <span className="text-text-subtle">{placeholder}</span>;
  }

  const visibleItems = maxVisible !== undefined ? value.slice(0, maxVisible) : value;
  const hiddenCount = maxVisible !== undefined ? Math.max(0, value.length - maxVisible) : 0;

  return (
    <span
      className={`flex flex-wrap gap-1 items-center ${maxVisible === undefined ? `overflow-y-auto ${maxHeightClassName}` : ''} ${className}`}
    >
      {visibleItems.map((item) => {
        const itemUrl = getItemUrl ? getItemUrl(item) : undefined;
        return (
          <span
            key={item.id}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-primary/10 text-primary text-[0.85rem] leading-5 max-w-full"
            title={item.name}
          >
            {itemUrl ? (
              <a
                href={itemUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="truncate hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 rounded-sm"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && !e.repeat) {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(itemUrl, '_blank', 'noopener,noreferrer');
                  }
                }}
                title={item.name}
              >
                {item.name}
              </a>
            ) : (
              <span className="truncate">{item.name}</span>
            )}
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                handleRemove(item.id);
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') {
                  return;
                }

                event.preventDefault();
                event.stopPropagation();
                handleRemove(item.id);
              }}
              className="inline-flex items-center justify-center rounded-full hover:bg-primary/20 focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed flex-shrink-0"
              aria-label={`Remove ${item.name}`}
              disabled={disabled}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        );
      })}
      {hiddenCount > 0 && (
        <span className="inline-flex items-center px-2 py-0.5 rounded bg-primary/10 text-primary text-[0.85rem] leading-5 font-medium whitespace-nowrap">
          +{hiddenCount} more
        </span>
      )}
    </span>
  );
};
